import type { UserLoginRo } from './api.generated';
import { exchangeSignupHandoff, HandoffSession } from './exchangeSignupHandoff';

/** Same origin rule as baseQuery, without importing the store. */
const getApiOrigin = (): string => {
  const configured = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_BASE_URL : process.env.REACT_APP_PRODUCTION_BASE_URL;
  if (!configured || configured === 'auto') return window.location.origin;
  return configured.replace(/\/$/, '');
};

/**
 * Public free-month calls (backend flag ENABLE_FREE_MONTH; contract
 * ops/free-month-api-contract-20261102.md). Plain fetch: the visitor has no
 * session. Hand-written until the next `yarn syncApis`.
 *
 * Nothing the visitor typed, no token and no password is ever put in a URL,
 * a log or a browser store; everything goes in a JSON body (or, for the
 * short session, an Authorization header).
 */

/**
 * Id of the privacy notice version shown on /start. Placeholder id until a
 * dated id is set: it must equal the backend FM-1 FREE_MONTH_CONSENT_VERSION,
 * bump both places together.
 */
export const FREE_MONTH_CONSENT_VERSION_FE = 'free-month-consent-2026-11-xx';
/** Must equal the backend FREE_MONTH_TERMS_VERSION / FREE_MONTH_DPA_VERSION. */
export const FREE_MONTH_TERMS_VERSION_FE = 'terms-v1';
export const FREE_MONTH_DPA_VERSION_FE = 'dpa-v1';

export type FreeMonthSignupStatus = { enabled: boolean; turnstileSiteKey?: string };

export type FreeMonthRequestBody = {
  name: string;
  email: string;
  organisation: string;
  locale: 'nl' | 'en';
  consentVersion: string;
  turnstileToken: string;
};

export type FreeMonthConfirmBody = {
  token: string;
  termsAccepted: true;
  authorityConfirmed: true;
  termsVersion: string;
  dpaVersion: string;
  locale: 'nl' | 'en';
};

/** What /start shows after the submit. */
export type FreeMonthRequestOutcome = 'check_email' | 'free_mail' | 'invalid' | 'cap_reached' | 'turnstile' | 'rate_limited' | 'error';

/** What the confirm page shows after the link check. */
export type FreeMonthCheckResult = { outcome: 'valid'; organisation: string } | { outcome: 'link_invalid' | 'rate_limited' | 'error' };

/** What the confirm page does after the button press. */
export type FreeMonthConfirmResult =
  | { outcome: 'confirmed'; handoff: string }
  | { outcome: 'link_invalid' | 'check_email' | 'free_mail' | 'invalid' | 'cap_reached' | 'rate_limited' | 'error' };

export type SetInitialPasswordResult =
  | { outcome: 'ok'; session: HandoffSession }
  | { outcome: 'password_already_set' | 'invalid' | 'unauthorized' | 'rate_limited' | 'error' };

const OFF: FreeMonthSignupStatus = { enabled: false };

const postJson = async (path: string, body: unknown, authToken?: string, apiOrigin?: string) => {
  const response = await fetch(`${apiOrigin ?? getApiOrigin()}/api/v1${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  return { response, json, code: typeof json?.code === 'string' ? (json.code as string) : '' };
};

/**
 * GET /billing/free-month/status; any failure reads as off (pages hidden).
 * `apiOrigin` (no trailing slash) is for the demo pop-up calling the PROD API
 * cross-origin; default is this app's own API origin.
 */
export const fetchFreeMonthStatus = async (apiOrigin?: string): Promise<FreeMonthSignupStatus> => {
  try {
    if (typeof fetch !== 'function') return OFF;
    const response = await fetch(`${apiOrigin ?? getApiOrigin()}/api/v1/billing/free-month/status`);
    if (!response.ok) return OFF;
    const body = await response.json();
    if (body?.enabled !== true) return OFF;
    return {
      enabled: true,
      ...(typeof body.turnstileSiteKey === 'string' && body.turnstileSiteKey ? { turnstileSiteKey: body.turnstileSiteKey } : {}),
    };
  } catch {
    return OFF;
  }
};

/** POST /billing/free-month (sends the verification mail). */
export const submitFreeMonthRequest = async (body: FreeMonthRequestBody, apiOrigin?: string): Promise<FreeMonthRequestOutcome> => {
  try {
    const { response, json, code } = await postJson('/billing/free-month', body, undefined, apiOrigin);
    if (response.ok) return json?.status === 'check_email' ? 'check_email' : 'error';
    if (response.status === 429) return 'rate_limited';
    if (code.startsWith('turnstile_')) return 'turnstile';
    if (code === 'free_mail') return 'free_mail';
    if (code === 'cap_reached') return 'cap_reached';
    if (response.status === 400 || code === 'invalid_details') return 'invalid';
    return 'error';
  } catch {
    return 'error';
  }
};

/** POST /billing/free-month/confirm/check: read-only preview, never consumes the token. */
export const checkFreeMonthLink = async (token: string): Promise<FreeMonthCheckResult> => {
  try {
    const { response, json } = await postJson('/billing/free-month/confirm/check', { token });
    if (response.ok) {
      if (json?.status === 'link_invalid') return { outcome: 'link_invalid' };
      if (json?.status === 'valid' && typeof json.organisation === 'string') return { outcome: 'valid', organisation: json.organisation };
      return { outcome: 'error' };
    }
    if (response.status === 429) return { outcome: 'rate_limited' };
    return { outcome: 'error' };
  } catch {
    return { outcome: 'error' };
  }
};

/** POST /billing/free-month/confirm: creates the account. Only on a button press. */
export const confirmFreeMonth = async (body: FreeMonthConfirmBody): Promise<FreeMonthConfirmResult> => {
  try {
    const { response, json, code } = await postJson('/billing/free-month/confirm', body);
    if (response.ok) {
      if (json?.status === 'confirmed' && typeof json.handoff === 'string' && json.handoff) return { outcome: 'confirmed', handoff: json.handoff };
      if (json?.status === 'link_invalid') return { outcome: 'link_invalid' };
      if (json?.status === 'check_email') return { outcome: 'check_email' };
      return { outcome: 'error' };
    }
    if (response.status === 429) return { outcome: 'rate_limited' };
    if (code === 'cap_reached') return { outcome: 'cap_reached' };
    if (code === 'free_mail') return { outcome: 'free_mail' };
    if (response.status === 400 || code === 'invalid_details') return { outcome: 'invalid' };
    return { outcome: 'error' };
  } catch {
    return { outcome: 'error' };
  }
};

/** POST /auth/handoff with the one-time code: the short (15 minute) session. */
export const exchangeFreeMonthHandoff = (handoff: string): Promise<HandoffSession> =>
  exchangeSignupHandoff({ handoff }, async (code) => {
    const { response, json } = await postJson('/auth/handoff', { handoff: code });
    if (!response.ok) throw new Error('Handoff failed');
    return json as { token: string; user: UserLoginRo };
  });

/** POST /auth/set-initial-password with the short session; 200 is a normal session. */
export const setInitialPassword = async (shortJwt: string, password: string): Promise<SetInitialPasswordResult> => {
  try {
    const { response, json, code } = await postJson('/auth/set-initial-password', { password }, shortJwt);
    if (response.ok) {
      return json?.token && json?.user ? { outcome: 'ok', session: { token: json.token, user: json.user } } : { outcome: 'error' };
    }
    if (response.status === 401) return { outcome: 'unauthorized' };
    if (response.status === 403 && code === 'password_already_set') return { outcome: 'password_already_set' };
    if (response.status === 429) return { outcome: 'rate_limited' };
    if (response.status === 400) return { outcome: 'invalid' };
    return { outcome: 'error' };
  } catch {
    return { outcome: 'error' };
  }
};
